import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Icon,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { MuiTelInput } from 'mui-tel-input';
import React from 'react';
import { Control, Controller, FieldErrors, FieldPath, UseFormRegister } from 'react-hook-form';

import AddressAutocomplete from '@/components/address-autocomplete';
import InputField from '@/components/InputField';
import SelectField from '@/components/SelectField';

import { SchemaType } from '../schema';

interface WithInvoiceProps<TName extends FieldPath<SchemaType> = FieldPath<SchemaType>> {
  register: UseFormRegister<SchemaType>;
  control?: Control<SchemaType>;
  errors?: FieldErrors<SchemaType>;
  data: WithInvoiceData<TName>;
  disabled?: boolean;
  country?: string;
}

interface WithInvoiceData<TName extends FieldPath<SchemaType> = FieldPath<SchemaType>> {
  name: TName;
  label: string;
  placeholder?: string;
  options?: { value: string; label: string }[];
  catalogOptions?: { Name: string; Value: string; Natural: boolean; Moral: boolean }[];
  companyType?: string;
  icon?: React.ReactNode;
  fullWidth?: boolean;
  type: 'input' | 'phone' | 'checkbox' | 'select' | 'direction';
  onCustomChange?: (value: string) => void;
}

const WithInvoiceForm = (props: WithInvoiceProps) => {
  const theme = useTheme();
  switch (props.data.type) {
    case 'phone':
      return (
        <Grid
          size={{
            xxs: 12,
            md: props.data.fullWidth ? 12 : 6,
            lg: 12,
            xxl: props.data.fullWidth ? 12 : 6,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', margin: '1rem 0.5rem 0.5rem 0' }}>
            {props.data.icon ? (
              <Icon color="disabled" sx={{ marginRight: '0.5rem' }}>
                {props.data.icon}
              </Icon>
            ) : null}
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.secondary.main,
                fontSize: '1rem',
              }}
            >
              {props.data.label}
            </Typography>
          </Box>
          <Controller
            name={props.data.name}
            control={props.control}
            render={({ field }) => (
              <MuiTelInput
                sx={{ width: '100%' }}
                onlyCountries={['MX', 'PE', 'CO', 'CL']}
                defaultCountry="MX"
                disabled={props.disabled}
                error={!!(props.errors?.company as any)?.phone}
                helperText={(props.errors?.company as any)?.phone?.message}
                value={typeof field.value === 'string' ? field.value : ''}
                name={field.name}
                onBlur={field.onBlur}
                ref={field.ref}
                onChange={(e) => {
                  field.onChange(e.replace(/\s/g, ''));
                }}
              />
            )}
          />
        </Grid>
      );
    case 'input': {
      const isPhoneField =
        props.data.name.includes('phone') ||
        props.data.label.toLowerCase().includes('teléfono') ||
        props.data.label.toLowerCase().includes('telefono');

      return (
        <Grid
          size={{
            xxs: 12,
            md: props.data.fullWidth ? 12 : 6,
            lg: 12,
            xxl: props.data.fullWidth ? 12 : 6,
          }}
        >
          <InputField
            label={props.data.label}
            name={props.data.name}
            errors={props.errors}
            register={props.register}
            icon={props.data.icon}
            sxInput={{ mt: 0 }}
            sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
            textFieldProps={{
              disabled: props.disabled,
              ...(isPhoneField
                ? {
                    inputProps: {
                      pattern: '[0-9]*',
                      inputMode: 'numeric',
                    },
                    onKeyPress: (event: React.KeyboardEvent) => {
                      if (
                        !/[0-9]/.test(event.key) &&
                        !['Backspace', 'Delete', 'Tab', 'Enter'].includes(event.key)
                      ) {
                        event.preventDefault();
                      }
                    },
                  }
                : {}),
            }}
          />
        </Grid>
      );
    }
    case 'checkbox':
      return (
        <Grid
          size={{
            xxs: 12,
            md: props.data.fullWidth ? 12 : 6,
            lg: 12,
            xxl: props.data.fullWidth ? 12 : 6,
          }}
          sx={{ height: '6.8125rem' }}
        >
          <Controller
            name={props.data.name}
            control={props.control}
            render={({ field }) => (
              <FormControl sx={{ gap: '1rem' }} error={!!props.errors}>
                <FormLabel
                  sx={{
                    fontWeight: 'bold',
                    color: theme.palette.secondary.main + ' !important',
                    fontSize: '1rem',
                    marginTop: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {props.data.icon ? props.data.icon : null}
                  {props.data.label}
                </FormLabel>
                <RadioGroup row {...field}>
                  {props.data.options?.map((option, index) => {
                    return (
                      <FormControlLabel
                        key={`${option.value}-${index}`}
                        value={option.value}
                        control={<Radio size="small" />}
                        sx={{ '.MuiFormControlLabel-label': { fontSize: '1rem' } }}
                        label={option.label}
                        disabled={props.disabled}
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
            )}
          />
        </Grid>
      );
    case 'select': {
      // Filter catalog options based on company type (same as client-console SelectCompany component)
      let selectOptions = props.data.options ?? [];

      if (props.data.catalogOptions && props.data.companyType) {
        const isNatural = props.data.companyType === 'naturalPerson';
        const isMoral = props.data.companyType === 'corporateEntity';

        // Filter out "Por definir" and filter by Natural/Moral like client-console
        const filteredCatalog = props.data.catalogOptions
          .filter((option) => option.Name !== 'Por definir')
          .filter((option) => {
            if (isNatural) return option.Natural === true;
            if (isMoral) return option.Moral === true;
            return false;
          })
          .map((option) => ({
            value: option.Value,
            label: option.Name,
          }));

        selectOptions = filteredCatalog;
      }

      return (
        <Grid
          size={{
            xxs: 12,
            md: props.data.fullWidth ? 12 : 6,
            lg: 12,
            xxl: props.data.fullWidth ? 12 : 6,
          }}
        >
          <Controller
            name={props.data.name}
            control={props.control}
            render={({ field }) => (
              <SelectField
                label={props.data.label}
                errors={props.errors}
                options={selectOptions}
                sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
                icon={props.data.icon}
                placeholder={props.data.placeholder}
                disabled={props.disabled}
                {...field}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  field.onChange(e);
                  // Si existe handler personalizado, ejecutarlo para capturar la descripción
                  if (props.data.onCustomChange) {
                    props.data.onCustomChange(e.target.value as string);
                  }
                }}
              />
            )}
          />
        </Grid>
      );
    }
    case 'direction':
      return (
        <Grid size={{ xxs: 12 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', margin: '1rem 0.5rem 0.5rem 0' }}>
            {props.data.icon ? props.data.icon : null}
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.secondary.main,
                fontSize: '1rem',
              }}
            >
              {props.data.label}
            </Typography>
          </Box>
          <Controller
            name={props.data.name}
            control={props.control}
            render={({ field, fieldState }) => (
              <AddressAutocomplete
                size="medium"
                value={typeof field.value === 'string' ? field.value : ''}
                placeholder={props.data.placeholder}
                country={props.country || 'MEX'}
                onSelectAddress={(details) => {
                  field.onChange(details.address);
                }}
                onChange={(value: string) => field.onChange(value)}
                disabled={props.disabled}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>
      );
    default:
      return <div />;
  }
};

export default WithInvoiceForm;
